/**
 * Wave 6 — 10 new city agency pages
 * Addis Ababa, Lusaka, Hanoi, Dhaka, Karachi, Medellín, Quito, Panama City, Tbilisi, Tallinn
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling. React, Node.js, Python, cloud-native.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools. Modern stack with React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring, and 24/7 managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists, and project managers integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance (ISO 27001, GDPR, HIPAA, PCI-DSS).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools, and banking applications.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management, and omnichannel retail solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine platforms, EHR integrations, and HIPAA-compliant health applications.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals, and marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility.' },
];

export const CITY_AGENCY_PAGES_WAVE6: Record<string, CityAgencyData> = {

  'addis-ababa': {
    slug: 'software-development-agency-addis-ababa',
    cityName: 'Addis Ababa',
    countryName: 'Ethiopia',
    flag: '🇪🇹',
    timezone: 'EAT (GMT+3)',
    currency: 'ETB/USD',
    title: {
      en: 'Software Development Agency Addis Ababa | Call IT Dev',
      fr: 'Agence Développement Logiciel Addis-Abeba | Call IT Dev',
      es: 'Agencia Desarrollo Software Addis Abeba | Call IT Dev',
      de: 'Softwareentwicklung Agentur Addis Abeba | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Addis Ababa, Ethiopia. Custom SaaS, web, mobile, AI. Growing tech hub, from $18/hr. AU headquarters city.',
      fr: 'Agence de développement logiciel à Addis-Abeba, Éthiopie. SaaS, web, mobile, IA. Hub tech en croissance, dès 18 $/h.',
      es: 'Agencia desarrollo software Addis Abeba, Etiopía. SaaS, web, móvil, IA. Hub tecnológico, desde 18 $/h.',
      de: 'Softwareentwicklungsagentur Addis Abeba, Äthiopien. SaaS, Web, Mobile, KI. Wachsender Tech-Hub, ab 18 $/h.',
    },
    keywords: ['software development Addis Ababa', 'IT outsourcing Ethiopia', 'tech hub Addis Ababa', 'web development Ethiopia'],
    heroTitle: {
      en: 'Software Development Agency in Addis Ababa',
      fr: 'Agence de Développement Logiciel à Addis-Abeba',
      es: 'Agencia de Desarrollo de Software en Addis Abeba',
      de: 'Softwareentwicklung Agentur in Addis Abeba',
    },
    heroSubtitle: {
      en: 'Africa\'s diplomatic capital meets its tech future. Young talent, competitive rates, and strategic positioning at the crossroads of Africa.',
      fr: 'La capitale diplomatique de l\'Afrique rencontre son avenir technologique. Jeunes talents et tarifs compétitifs.',
      es: 'La capital diplomática de África encuentra su futuro tecnológico. Talento joven y tarifas competitivas.',
      de: 'Afrikas diplomatische Hauptstadt trifft auf Technologie-Zukunft. Junge Talente und wettbewerbsfähige Preise.',
    },
    whyTitle: {
      en: 'Why Choose Addis Ababa for Software Development',
      fr: 'Pourquoi Choisir Addis-Abeba pour le Développement Logiciel',
      es: 'Por Qué Elegir Addis Abeba para Desarrollo de Software',
      de: 'Warum Addis Abeba für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '🌍', title: 'AU Capital & Tech Hub', description: 'Home to the African Union and a rapidly growing tech ecosystem with government-backed digital initiatives.' },
      { icon: '💰', title: 'Highly Competitive Rates', description: 'Senior developers at $18-30/hr, among the most competitive rates in the global market.' },
      { icon: '🎓', title: 'Young Tech Talent', description: 'Large pool of university-trained engineers from top Ethiopian institutions and international programs.' },
      { icon: '✈️', title: 'Ethiopian Airlines Hub', description: 'Africa\'s largest airline connects Addis Ababa to 130+ destinations worldwide.' },
      { icon: '⏰', title: 'EAT Timezone', description: 'GMT+3 enables same-day collaboration with Europe and Middle East.' },
      { icon: '📈', title: 'Fastest Growing Economy', description: 'One of Africa\'s fastest-growing economies with massive investment in digital infrastructure.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Addis Ababa for software development?', answer: 'Addis Ababa combines a young, educated workforce, competitive rates ($18-30/hr), and strong connectivity via Ethiopian Airlines to 130+ global destinations.' },
      { question: 'What is the internet infrastructure like?', answer: 'Addis Ababa has modern fiber-optic infrastructure with government investment in 5G and data centers. Our teams use redundant connectivity.' },
      { question: 'What languages do your engineers speak?', answer: 'English and Amharic primarily. Many engineers also speak French, making them versatile for international projects.' },
      { question: 'How do you ensure quality?', answer: 'Code reviews, CI/CD pipelines, 80%+ test coverage, and bi-weekly demos. ISO 27001-aligned processes.' },
      { question: 'Can I visit?', answer: 'Yes! Ethiopian Airlines offers direct flights from most major cities. Our office is in Addis Ababa\'s tech district.' },
      { question: 'What about data protection?', answer: 'We follow GDPR-aligned practices with signed NDAs and DPAs. All code IP belongs to you.' },
    ],
    localRate: '$60-90',
    ourRate: '$18-30',
    savings: '65%+',
  },

  lusaka: {
    slug: 'software-development-agency-lusaka',
    cityName: 'Lusaka',
    countryName: 'Zambia',
    flag: '🇿🇲',
    timezone: 'CAT (GMT+2)',
    currency: 'ZMW/USD',
    title: {
      en: 'Software Development Agency Lusaka | Call IT Dev',
      fr: 'Agence Développement Logiciel Lusaka | Call IT Dev',
      es: 'Agencia Desarrollo Software Lusaka | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Lusaka, Zambia. Custom SaaS, web, mobile, AI. English-speaking engineers, from $18/hr.',
      fr: 'Agence développement logiciel à Lusaka, Zambie. SaaS, web, mobile, IA. Ingénieurs anglophones, dès 18 $/h.',
      es: 'Agencia desarrollo software Lusaka, Zambia. SaaS, web, móvil, IA. Ingenieros anglófonos, desde 18 $/h.',
    },
    keywords: ['software development Lusaka', 'IT outsourcing Zambia', 'web development Lusaka', 'tech talent Zambia'],
    heroTitle: {
      en: 'Software Development Agency in Lusaka',
      fr: 'Agence de Développement Logiciel à Lusaka',
      es: 'Agencia de Desarrollo de Software en Lusaka',
    },
    heroSubtitle: {
      en: 'English-speaking African tech talent. Competitive rates, growing digital ecosystem, and strategic Southern Africa positioning.',
      fr: 'Talents tech africains anglophones. Tarifs compétitifs et écosystème digital en croissance.',
      es: 'Talento tech africano anglófono. Tarifas competitivas y ecosistema digital en crecimiento.',
    },
    whyTitle: {
      en: 'Why Choose Lusaka for Software Development',
      fr: 'Pourquoi Choisir Lusaka pour le Développement Logiciel',
      es: 'Por Qué Elegir Lusaka para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '🗣️', title: 'English-Speaking Nation', description: 'Zambia is natively English-speaking, ensuring seamless communication with UK, US, and international clients.' },
      { icon: '💰', title: 'Cost-Effective Talent', description: 'Senior developers at $18-28/hr, one of the most affordable in the English-speaking world.' },
      { icon: '🌍', title: 'Southern Africa Hub', description: 'Strategic position in Southern Africa with growing fintech and agritech sectors.' },
      { icon: '📈', title: 'Digital Growth', description: 'Government-backed Smart Zambia initiative driving digital transformation and tech investment.' },
      { icon: '⏰', title: 'GMT+2 Timezone', description: 'Ideal overlap with European and Middle Eastern business hours.' },
      { icon: '🎓', title: 'University Talent', description: 'Graduates from University of Zambia, Copperbelt University, and international programs.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Lusaka for development?', answer: 'Native English speakers, competitive rates, GMT+2 timezone, and a growing tech ecosystem backed by government digital initiatives.' },
      { question: 'What is internet quality like?', answer: 'Our teams use fiber-optic connections with backup links. Lusaka has modern connectivity infrastructure.' },
      { question: 'What industries do you serve?', answer: 'Fintech, agritech, e-commerce, healthcare, and SaaS. Strong local expertise in mobile-first solutions.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2 weeks. CVs and technical interviews within 5 business days.' },
      { question: 'Do you handle compliance?', answer: 'Yes — GDPR-aligned, ISO 27001 practices, signed NDAs, and full IP transfer.' },
      { question: 'What is the minimum engagement?', answer: 'Start with 2 developers. Scale as needed with no long-term lock-in.' },
    ],
    localRate: '$60-80',
    ourRate: '$18-28',
    savings: '60%+',
  },

  hanoi: {
    slug: 'software-development-agency-hanoi',
    cityName: 'Hanoi',
    countryName: 'Vietnam',
    flag: '🇻🇳',
    timezone: 'ICT (GMT+7)',
    currency: 'VND/USD',
    title: {
      en: 'Software Development Agency Hanoi | Call IT Dev',
      fr: 'Agence Développement Logiciel Hanoï | Call IT Dev',
      es: 'Agencia Desarrollo Software Hanói | Call IT Dev',
      de: 'Softwareentwicklung Agentur Hanoi | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Hanoi, Vietnam. Custom SaaS, web, mobile, AI. 500K+ IT workforce, from $20/hr.',
      fr: 'Agence développement logiciel à Hanoï, Vietnam. SaaS, web, mobile, IA. 500K+ professionnels IT, dès 20 $/h.',
      es: 'Agencia desarrollo software Hanói, Vietnam. SaaS, web, móvil, IA. 500K+ profesionales IT, desde 20 $/h.',
      de: 'Softwareentwicklungsagentur Hanoi, Vietnam. SaaS, Web, Mobile, KI. 500K+ IT-Fachkräfte, ab 20 $/h.',
    },
    keywords: ['software development Hanoi', 'IT outsourcing Vietnam', 'web development Hanoi', 'nearshore Vietnam', 'offshore development Hanoi'],
    heroTitle: {
      en: 'Software Development Agency in Hanoi',
      fr: 'Agence de Développement Logiciel à Hanoï',
      es: 'Agencia de Desarrollo de Software en Hanói',
      de: 'Softwareentwicklung Agentur in Hanoi',
    },
    heroSubtitle: {
      en: 'Vietnam\'s capital and northern tech powerhouse. World-class engineering talent, government-backed IT sector, and unbeatable rates.',
      fr: 'Capitale du Vietnam et puissance technologique du nord. Talents d\'ingénierie de classe mondiale et tarifs imbattables.',
      es: 'Capital de Vietnam y potencia tecnológica del norte. Talento de ingeniería de clase mundial y tarifas inmejorables.',
      de: 'Vietnams Hauptstadt und nördliche Tech-Hochburg. Erstklassige Ingenieure und unschlagbare Preise.',
    },
    whyTitle: {
      en: 'Why Choose Hanoi for Software Development',
      fr: 'Pourquoi Choisir Hanoï pour le Développement Logiciel',
      es: 'Por Qué Elegir Hanói para Desarrollo de Software',
      de: 'Warum Hanoi für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'Top STEM Graduates', description: 'Hanoi University of Science and Technology and VNU produce world-class engineers every year.' },
      { icon: '💰', title: 'Excellent Value', description: 'Senior developers at $20-35/hr with quality matching Eastern Europe.' },
      { icon: '🏗️', title: 'Government Support', description: 'Vietnam\'s IT sector backed by Resolution 36 and National Digital Transformation Program.' },
      { icon: '📈', title: 'Proven Track Record', description: 'Vietnam ranked #1 in SE Asia for software outsourcing. Samsung, Intel, and FPT major presence.' },
      { icon: '⏰', title: 'GMT+7 Coverage', description: 'Enables follow-the-sun development with European and US teams.' },
      { icon: '🔒', title: 'IP Protection', description: 'Vietnam\'s IP laws aligned with WTO standards. Strong legal framework for tech contracts.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Hanoi over Ho Chi Minh City?', answer: 'Hanoi is Vietnam\'s political capital with top universities and lower cost of living, resulting in slightly lower rates. Both cities have excellent talent.' },
      { question: 'What technologies do Hanoi engineers excel at?', answer: 'React, Node.js, Java, Python, .NET, mobile (React Native, Flutter), AI/ML, and blockchain. Strong in fintech and e-commerce.' },
      { question: 'How is the timezone managed?', answer: 'GMT+7 enables 3-4 hours overlap with European afternoons and full overlap with Asian markets. We offer flexible hours for US clients.' },
      { question: 'What about data security?', answer: 'ISO 27001-aligned, GDPR practices, signed NDAs. Vietnam\'s Cybersecurity Law ensures strong data protection.' },
      { question: 'Can I visit the Hanoi office?', answer: 'Yes! Hanoi is well-connected with direct flights from major Asian and European hubs.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers. We scale teams from 2 to 50+ engineers.' },
    ],
    localRate: '$50-80',
    ourRate: '$20-35',
    savings: '55%+',
  },

  dhaka: {
    slug: 'software-development-agency-dhaka',
    cityName: 'Dhaka',
    countryName: 'Bangladesh',
    flag: '🇧🇩',
    timezone: 'BST (GMT+6)',
    currency: 'BDT/USD',
    title: {
      en: 'Software Development Agency Dhaka | Call IT Dev',
      fr: 'Agence Développement Logiciel Dacca | Call IT Dev',
      es: 'Agencia Desarrollo Software Daca | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Dhaka, Bangladesh. Custom SaaS, web, mobile, AI. 600K+ IT professionals, from $15/hr.',
      fr: 'Agence développement logiciel à Dacca, Bangladesh. SaaS, web, mobile, IA. 600K+ professionnels IT, dès 15 $/h.',
      es: 'Agencia desarrollo software Daca, Bangladesh. SaaS, web, móvil, IA. 600K+ profesionales IT, desde 15 $/h.',
    },
    keywords: ['software development Dhaka', 'IT outsourcing Bangladesh', 'web development Dhaka', 'offshore development Bangladesh'],
    heroTitle: {
      en: 'Software Development Agency in Dhaka',
      fr: 'Agence de Développement Logiciel à Dacca',
      es: 'Agencia de Desarrollo de Software en Daca',
    },
    heroSubtitle: {
      en: 'Bangladesh\'s booming IT sector. Massive talent pool, highly competitive rates, and a government-backed vision for digital transformation.',
      fr: 'Le secteur IT en plein boom du Bangladesh. Vaste réservoir de talents et tarifs ultra-compétitifs.',
      es: 'El sector IT en auge de Bangladesh. Gran reserva de talento y tarifas ultra-competitivas.',
    },
    whyTitle: {
      en: 'Why Choose Dhaka for Software Development',
      fr: 'Pourquoi Choisir Dacca pour le Développement Logiciel',
      es: 'Por Qué Elegir Daca para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '👥', title: 'Massive Talent Pool', description: '600K+ IT professionals and 130+ universities producing STEM graduates.' },
      { icon: '💰', title: 'Ultra-Competitive Rates', description: 'Senior developers at $15-25/hr — among the lowest globally for quality work.' },
      { icon: '📈', title: 'Digital Bangladesh Vision', description: 'Government\'s Digital Bangladesh initiative driving tech infrastructure and education.' },
      { icon: '🏗️', title: 'Hi-Tech Parks', description: 'Dedicated IT parks with world-class infrastructure, reliable power and internet.' },
      { icon: '🗣️', title: 'English Proficiency', description: 'English widely spoken in business and tech. Strong communication skills.' },
      { icon: '⏰', title: 'GMT+6 Coverage', description: 'Good overlap with Asian markets and European mornings.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Dhaka for software development?', answer: 'Dhaka offers a massive IT talent pool (600K+), ultra-competitive rates ($15-25/hr), English proficiency, and government-backed tech infrastructure.' },
      { question: 'What is the quality level?', answer: 'Bangladesh ranks among the top freelancer nations globally. Our teams follow strict CI/CD, code review, and testing standards.' },
      { question: 'How reliable is internet?', answer: 'Our teams operate from Hi-Tech Parks with redundant fiber connections, backup power, and enterprise-grade infrastructure.' },
      { question: 'What industries do you specialize in?', answer: 'Garment-tech, fintech, e-commerce, mobile-first applications, and SaaS platforms.' },
      { question: 'How fast can a team start?', answer: '2-week setup. CVs within 5 days, interviews week 1, first sprint week 3.' },
      { question: 'Data security?', answer: 'ISO 27001-aligned, GDPR practices, NDAs, and full IP ownership transfer to clients.' },
    ],
    localRate: '$50-70',
    ourRate: '$15-25',
    savings: '60%+',
  },

  karachi: {
    slug: 'software-development-agency-karachi',
    cityName: 'Karachi',
    countryName: 'Pakistan',
    flag: '🇵🇰',
    timezone: 'PKT (GMT+5)',
    currency: 'PKR/USD',
    title: {
      en: 'Software Development Agency Karachi | Call IT Dev',
      fr: 'Agence Développement Logiciel Karachi | Call IT Dev',
      es: 'Agencia Desarrollo Software Karachi | Call IT Dev',
      ar: 'وكالة تطوير برمجيات كراتشي | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Karachi, Pakistan. Custom SaaS, web, mobile, AI. 300K+ IT workforce, from $18/hr.',
      fr: 'Agence développement logiciel à Karachi, Pakistan. SaaS, web, mobile, IA. 300K+ professionnels IT, dès 18 $/h.',
      es: 'Agencia desarrollo software Karachi, Pakistán. SaaS, web, móvil, IA. 300K+ profesionales IT, desde 18 $/h.',
      ar: 'وكالة تطوير برمجيات في كراتشي، باكستان. SaaS، ويب، موبايل. 300K+ محترف تقنية.',
    },
    keywords: ['software development Karachi', 'IT outsourcing Pakistan', 'web development Karachi', 'offshore Pakistan'],
    heroTitle: {
      en: 'Software Development Agency in Karachi',
      fr: 'Agence de Développement Logiciel à Karachi',
      es: 'Agencia de Desarrollo de Software en Karachi',
      ar: 'وكالة تطوير برمجيات في كراتشي',
    },
    heroSubtitle: {
      en: 'Pakistan\'s commercial capital and largest tech hub. Strong engineering culture, English fluency, and exceptional value.',
      fr: 'Capitale commerciale du Pakistan et plus grand hub tech. Culture ingénierie solide et valeur exceptionnelle.',
      es: 'Capital comercial de Pakistán y mayor hub tecnológico. Fuerte cultura de ingeniería y valor excepcional.',
      ar: 'العاصمة التجارية لباكستان وأكبر مركز تكنولوجي. ثقافة هندسية قوية وقيمة استثنائية.',
    },
    whyTitle: {
      en: 'Why Choose Karachi for Software Development',
      fr: 'Pourquoi Choisir Karachi pour le Développement Logiciel',
      es: 'Por Qué Elegir Karachi para Desarrollo de Software',
      ar: 'لماذا تختار كراتشي لتطوير البرمجيات',
    },
    whyReasons: [
      { icon: '🎓', title: 'Engineering Excellence', description: 'NED, FAST, IBA, and LUMS produce world-class computer science graduates.' },
      { icon: '💰', title: 'Excellent Value', description: 'Senior developers at $18-30/hr with strong algorithmic and system design skills.' },
      { icon: '🗣️', title: 'English Fluency', description: 'English is the business language. Excellent written and verbal communication.' },
      { icon: '📈', title: 'Freelance Capital', description: 'Pakistan is #4 globally for freelancers. Deep culture of remote work and delivery.' },
      { icon: '⏰', title: 'GMT+5 Coverage', description: 'Good overlap with Europe, Middle East, and partial US overlap.' },
      { icon: '🔒', title: 'Growing Tech Ecosystem', description: 'Government IT parks, Special Technology Zones, and foreign investment in tech.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Karachi for development?', answer: 'Karachi offers world-class engineering talent from top universities, strong English fluency, competitive rates ($18-30/hr), and a proven track record in global freelancing.' },
      { question: 'What is Pakistan\'s IT reputation?', answer: 'Pakistan is the 4th largest freelancer nation globally. Major tech companies have development centers here. The sector exports $2.5B+ annually.' },
      { question: 'How do you handle time zones?', answer: 'GMT+5 provides overlap with European afternoons and Middle East. We offer flexible hours for US clients including evening shifts.' },
      { question: 'What about security?', answer: 'ISO 27001-aligned, GDPR practices, signed NDAs, encrypted communications. Teams operate from secure tech parks.' },
      { question: 'Minimum engagement?', answer: '2 developers minimum, scale to 30+. Flexible monthly engagement.' },
      { question: 'Can I visit?', answer: 'Yes! Karachi has direct flights from Dubai (2h), major Asian and European cities.' },
    ],
    localRate: '$50-80',
    ourRate: '$18-30',
    savings: '60%+',
  },

  medellin: {
    slug: 'software-development-agency-medellin',
    cityName: 'Medellín',
    countryName: 'Colombia',
    flag: '🇨🇴',
    timezone: 'COT (GMT-5)',
    currency: 'COP/USD',
    title: {
      en: 'Software Development Agency Medellín | Call IT Dev',
      fr: 'Agence Développement Logiciel Medellín | Call IT Dev',
      es: 'Agencia Desarrollo Software Medellín | Call IT Dev',
      pt: 'Agência Desenvolvimento Software Medellín | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Medellín, Colombia. Custom SaaS, web, mobile, AI. Innovation capital, from $22/hr. Same US timezone.',
      fr: 'Agence développement logiciel à Medellín, Colombie. SaaS, web, mobile, IA. Capitale de l\'innovation, dès 22 $/h.',
      es: 'Agencia desarrollo software Medellín, Colombia. SaaS, web, móvil, IA. Capital de la innovación, desde 22 $/h.',
      pt: 'Agência desenvolvimento software Medellín, Colômbia. SaaS, web, mobile, IA. Capital da inovação, desde 22 $/h.',
    },
    keywords: ['software development Medellín', 'IT outsourcing Colombia', 'nearshore development Medellín', 'tech talent Colombia', 'agencia desarrollo Medellín'],
    heroTitle: {
      en: 'Software Development Agency in Medellín',
      fr: 'Agence de Développement Logiciel à Medellín',
      es: 'Agencia de Desarrollo de Software en Medellín',
      pt: 'Agência de Desenvolvimento de Software em Medellín',
    },
    heroSubtitle: {
      en: 'Colombia\'s innovation capital. Top-tier LATAM talent, US timezone alignment, and a world-renowned tech transformation story.',
      fr: 'Capitale de l\'innovation colombienne. Talents LATAM de premier plan et alignement fuseau US.',
      es: 'Capital de la innovación de Colombia. Talento LATAM de primer nivel y alineación horaria con EE.UU.',
      pt: 'Capital da inovação colombiana. Talentos LATAM de primeira linha e alinhamento com fuso dos EUA.',
    },
    whyTitle: {
      en: 'Why Choose Medellín for Software Development',
      fr: 'Pourquoi Choisir Medellín pour le Développement Logiciel',
      es: 'Por Qué Elegir Medellín para Desarrollo de Software',
      pt: 'Por Que Escolher Medellín para Desenvolvimento de Software',
    },
    whyReasons: [
      { icon: '🏆', title: 'Innovation Capital', description: 'Named most innovative city by WSJ. Ruta N technology district and thriving startup ecosystem.' },
      { icon: '⏰', title: 'US Timezone (EST)', description: 'Same timezone as New York. Perfect for real-time US collaboration.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Senior developers at $22-38/hr with strong CS fundamentals and modern stack expertise.' },
      { icon: '🎓', title: 'Top Universities', description: 'EAFIT, UdeA, and UPB produce excellent engineers. Strong talent pipeline.' },
      { icon: '🌐', title: 'Bilingual Teams', description: 'Growing English proficiency in the tech sector. Spanish-English bilingual developers available.' },
      { icon: '🏗️', title: 'Tech Infrastructure', description: 'Ruta N district, co-working spaces, fiber optic, and government tech incentives.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Medellín over Bogotá?', answer: 'Medellín is Colombia\'s innovation hub with Ruta N, lower cost of living, better quality of life attracting top talent, and the same timezone advantage.' },
      { question: 'What is the tech scene like?', answer: 'Medellín was named most innovative city by WSJ. Home to 500+ tech companies, accelerators, and a vibrant startup ecosystem.' },
      { question: 'Do engineers speak English?', answer: 'Growing English proficiency. We specifically recruit bilingual engineers (Spanish-English) for international projects.' },
      { question: 'How does nearshore work for US companies?', answer: 'Same timezone (EST/COT), real-time collaboration, 3-4 hour flights from Miami. Cultural proximity and similar work culture.' },
      { question: 'Can I visit?', answer: 'Absolutely! Direct flights from Miami (3h), NYC (5h). Medellín has excellent infrastructure and is very welcoming to visitors.' },
      { question: 'What about IP protection?', answer: 'Colombia has strong IP laws aligned with US standards. Full IP transfer, NDAs, and GDPR-compliant practices.' },
    ],
    localRate: '$50-80',
    ourRate: '$22-38',
    savings: '55%+',
  },

  quito: {
    slug: 'software-development-agency-quito',
    cityName: 'Quito',
    countryName: 'Ecuador',
    flag: '🇪🇨',
    timezone: 'ECT (GMT-5)',
    currency: 'USD',
    title: {
      en: 'Software Development Agency Quito | Call IT Dev',
      fr: 'Agence Développement Logiciel Quito | Call IT Dev',
      es: 'Agencia Desarrollo Software Quito | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Quito, Ecuador. Custom SaaS, web, mobile, AI. US timezone, dollarized economy, from $20/hr.',
      fr: 'Agence développement logiciel à Quito, Équateur. SaaS, web, mobile, IA. Fuseau US, économie dollarisée, dès 20 $/h.',
      es: 'Agencia desarrollo software Quito, Ecuador. SaaS, web, móvil, IA. Zona horaria US, economía dolarizada, desde 20 $/h.',
    },
    keywords: ['software development Quito', 'IT outsourcing Ecuador', 'nearshore development Ecuador', 'web development Quito'],
    heroTitle: {
      en: 'Software Development Agency in Quito',
      fr: 'Agence de Développement Logiciel à Quito',
      es: 'Agencia de Desarrollo de Software en Quito',
    },
    heroSubtitle: {
      en: 'Ecuador\'s tech-savvy capital. US timezone alignment, dollarized economy, and a growing pool of skilled engineers.',
      fr: 'Capitale technologique de l\'Équateur. Alignement fuseau US, économie dollarisée et ingénieurs qualifiés.',
      es: 'Capital tecnológica de Ecuador. Zona horaria US, economía dolarizada y creciente pool de ingenieros.',
    },
    whyTitle: {
      en: 'Why Choose Quito for Software Development',
      fr: 'Pourquoi Choisir Quito pour le Développement Logiciel',
      es: 'Por Qué Elegir Quito para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '💵', title: 'Dollarized Economy', description: 'Ecuador uses USD — no currency risk, simplified invoicing and payments.' },
      { icon: '⏰', title: 'US Timezone (EST)', description: 'Same timezone as New York and Miami. Perfect for real-time collaboration.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Senior developers at $20-35/hr with strong web and mobile development skills.' },
      { icon: '🎓', title: 'Technical Talent', description: 'EPN, ESPE, and USFQ universities produce quality CS graduates.' },
      { icon: '🌐', title: 'Spanish & English', description: 'Bilingual talent available for international projects.' },
      { icon: '🏔️', title: 'Quality of Life', description: 'UNESCO World Heritage city with low cost of living attracting and retaining top talent.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Quito?', answer: 'Quito offers US timezone alignment, a dollarized economy (no FX risk), competitive rates ($20-35/hr), and growing tech talent.' },
      { question: 'What is the advantage of dollarization?', answer: 'No currency conversion costs, no exchange rate risk. Invoicing and payments in USD, just like US-based vendors.' },
      { question: 'Do engineers speak English?', answer: 'We recruit bilingual Spanish-English engineers for international projects. Growing English proficiency in the tech sector.' },
      { question: 'How is the connectivity?', answer: 'Quito has modern fiber-optic infrastructure. Our teams use redundant connections for reliable uptime.' },
      { question: 'Can I visit?', answer: 'Yes! Direct flights from Miami (4h), Houston (5h). Quito is a beautiful UNESCO heritage city.' },
      { question: 'What about IP and contracts?', answer: 'Full IP ownership transfer, NDAs, and GDPR-aligned practices. Ecuador has IP protection laws.' },
    ],
    localRate: '$50-70',
    ourRate: '$20-35',
    savings: '55%+',
  },

  'panama-city': {
    slug: 'software-development-agency-panama-city',
    cityName: 'Panama City',
    countryName: 'Panama',
    flag: '🇵🇦',
    timezone: 'EST (GMT-5)',
    currency: 'USD/PAB',
    title: {
      en: 'Software Development Agency Panama City | Call IT Dev',
      fr: 'Agence Développement Logiciel Panama | Call IT Dev',
      es: 'Agencia Desarrollo Software Ciudad de Panamá | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Panama City. Custom SaaS, web, mobile, AI. Americas business hub, US timezone, dollarized, from $25/hr.',
      fr: 'Agence développement logiciel à Panama. SaaS, web, mobile, IA. Hub business des Amériques, dès 25 $/h.',
      es: 'Agencia desarrollo software Ciudad de Panamá. SaaS, web, móvil, IA. Hub de negocios, zona horaria US, desde 25 $/h.',
    },
    keywords: ['software development Panama', 'IT outsourcing Panama', 'nearshore development Panama', 'web development Panama City'],
    heroTitle: {
      en: 'Software Development Agency in Panama City',
      fr: 'Agence de Développement Logiciel à Panama',
      es: 'Agencia de Desarrollo de Software en Ciudad de Panamá',
    },
    heroSubtitle: {
      en: 'The Americas\' business crossroads. US timezone, dollarized economy, tax advantages, and bilingual talent at the bridge of two continents.',
      fr: 'Le carrefour des affaires des Amériques. Fuseau US, économie dollarisée et talents bilingues.',
      es: 'El cruce de negocios de las Américas. Zona horaria US, economía dolarizada y talento bilingüe.',
    },
    whyTitle: {
      en: 'Why Choose Panama City for Software Development',
      fr: 'Pourquoi Choisir Panama pour le Développement Logiciel',
      es: 'Por Qué Elegir Ciudad de Panamá para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '🌎', title: 'Americas Hub', description: 'Strategic position connecting North and South America. Major business and logistics hub.' },
      { icon: '💵', title: 'USD Economy', description: 'Dollarized economy, no currency risk. Favorable tax environment for tech companies.' },
      { icon: '⏰', title: 'EST Timezone', description: 'Same timezone as New York. Perfect overlap with US East Coast clients.' },
      { icon: '🗣️', title: 'Bilingual Talent', description: 'Strong English-Spanish bilingual workforce in the tech sector.' },
      { icon: '✈️', title: 'Copa Hub', description: 'Copa Airlines connects Panama to 80+ destinations in the Americas.' },
      { icon: '📋', title: 'Business-Friendly', description: 'Stable economy, strong legal framework, and government incentives for tech.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Panama for development?', answer: 'Panama offers US timezone, USD economy, bilingual talent, tax advantages, and strategic Americas positioning — ideal for nearshore development.' },
      { question: 'What are the tax advantages?', answer: 'Panama has favorable corporate tax laws and no taxes on foreign-sourced income. Special economic zones offer additional incentives.' },
      { question: 'Do engineers speak English?', answer: 'Yes, Panama has strong English proficiency especially in the tech and business sectors. Bilingual teams standard.' },
      { question: 'How is connectivity?', answer: 'Panama has one of the best internet infrastructures in Central America with submarine cable connections.' },
      { question: 'Can I visit?', answer: 'Yes! Copa Airlines hub offers direct flights to most Americas cities. Miami is 3h away.' },
      { question: 'IP protection?', answer: 'Panama has robust IP laws. Full IP transfer, NDAs, and international-standard contracts.' },
    ],
    localRate: '$50-80',
    ourRate: '$25-40',
    savings: '50%+',
  },

  tbilisi: {
    slug: 'software-development-agency-tbilisi',
    cityName: 'Tbilisi',
    countryName: 'Georgia',
    flag: '🇬🇪',
    timezone: 'GET (GMT+4)',
    currency: 'GEL/USD',
    title: {
      en: 'Software Development Agency Tbilisi | Call IT Dev',
      fr: 'Agence Développement Logiciel Tbilissi | Call IT Dev',
      es: 'Agencia Desarrollo Software Tiflis | Call IT Dev',
      de: 'Softwareentwicklung Agentur Tiflis | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Tbilisi, Georgia. Custom SaaS, web, mobile, AI. EU-aligned, from $22/hr. Emerging Eastern European tech hub.',
      fr: 'Agence développement logiciel à Tbilissi, Géorgie. SaaS, web, mobile, IA. Alignée UE, dès 22 $/h.',
      es: 'Agencia desarrollo software Tiflis, Georgia. SaaS, web, móvil, IA. Alineada con la UE, desde 22 $/h.',
      de: 'Softwareentwicklungsagentur Tiflis, Georgien. SaaS, Web, Mobile, KI. EU-orientiert, ab 22 $/h.',
    },
    keywords: ['software development Tbilisi', 'IT outsourcing Georgia', 'web development Tbilisi', 'nearshore development Georgia'],
    heroTitle: {
      en: 'Software Development Agency in Tbilisi',
      fr: 'Agence de Développement Logiciel à Tbilissi',
      es: 'Agencia de Desarrollo de Software en Tiflis',
      de: 'Softwareentwicklung Agentur in Tiflis',
    },
    heroSubtitle: {
      en: 'Georgia\'s vibrant capital and emerging tech destination. EU-aligned regulations, strong engineering heritage, and excellent quality-to-cost ratio.',
      fr: 'Capitale vibrante de la Géorgie et destination tech émergente. Réglementations alignées UE et excellent ratio qualité-prix.',
      es: 'Vibrante capital de Georgia y destino tech emergente. Regulaciones alineadas con la UE y excelente ratio calidad-precio.',
      de: 'Georgiens lebendige Hauptstadt und aufstrebendes Tech-Ziel. EU-konforme Regulierung und exzellentes Preis-Leistungs-Verhältnis.',
    },
    whyTitle: {
      en: 'Why Choose Tbilisi for Software Development',
      fr: 'Pourquoi Choisir Tbilissi pour le Développement Logiciel',
      es: 'Por Qué Elegir Tiflis para Desarrollo de Software',
      de: 'Warum Tiflis für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '🇪🇺', title: 'EU-Aligned', description: 'Georgia is an EU candidate with GDPR-aligned data protection laws and European business standards.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Senior developers at $22-35/hr with Soviet-era strong mathematical and engineering education heritage.' },
      { icon: '🎓', title: 'Engineering Heritage', description: 'Georgian Technical University and Tbilisi State University produce excellent STEM graduates.' },
      { icon: '🌐', title: 'Multilingual', description: 'Engineers speak English, Russian, and Georgian. Growing international orientation.' },
      { icon: '⏰', title: 'GMT+4 Coverage', description: 'Ideal overlap with European and Middle Eastern business hours.' },
      { icon: '🏗️', title: 'Tech-Friendly Environment', description: 'Low taxes, easy business registration, and growing number of tech companies and startups.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Tbilisi?', answer: 'Tbilisi combines EU-aligned regulations, strong engineering talent, competitive rates ($22-35/hr), and excellent business environment with low taxes.' },
      { question: 'What is Georgia\'s tech ecosystem like?', answer: 'Rapidly growing with government support, international accelerators, and companies like Flat Rock Technology and Sweeft Digital.' },
      { question: 'Do engineers speak English?', answer: 'Yes, younger generation engineers have strong English skills. Many also speak Russian for CIS market projects.' },
      { question: 'Is Georgia EU?', answer: 'Georgia is an EU candidate country with DCFTA agreement. It follows EU standards for data protection and business practices.' },
      { question: 'How to visit?', answer: 'Direct flights from most European cities. Georgia offers visa-free entry for 95+ countries.' },
      { question: 'Data protection?', answer: 'Georgia has GDPR-aligned data protection law. ISO 27001 practices, NDAs, and full IP ownership transfer.' },
    ],
    localRate: '$50-70',
    ourRate: '$22-35',
    savings: '55%+',
  },

  tallinn: {
    slug: 'software-development-agency-tallinn',
    cityName: 'Tallinn',
    countryName: 'Estonia',
    flag: '🇪🇪',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Tallinn | Call IT Dev',
      fr: 'Agence Développement Logiciel Tallinn | Call IT Dev',
      es: 'Agencia Desarrollo Software Tallin | Call IT Dev',
      de: 'Softwareentwicklung Agentur Tallinn | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Tallinn, Estonia. Custom SaaS, web, mobile, AI. Digital nation pioneer, EU member, from $30/hr.',
      fr: 'Agence développement logiciel à Tallinn, Estonie. SaaS, web, mobile, IA. Pionnière du numérique, UE, dès 30 $/h.',
      es: 'Agencia desarrollo software Tallin, Estonia. SaaS, web, móvil, IA. Pionera digital, UE, desde 30 $/h.',
      de: 'Softwareentwicklungsagentur Tallinn, Estland. SaaS, Web, Mobile, KI. Digitaler Pionier, EU, ab 30 $/h.',
    },
    keywords: ['software development Tallinn', 'IT outsourcing Estonia', 'web development Tallinn', 'e-residency development', 'nearshore Estonia'],
    heroTitle: {
      en: 'Software Development Agency in Tallinn',
      fr: 'Agence de Développement Logiciel à Tallinn',
      es: 'Agencia de Desarrollo de Software en Tallin',
      de: 'Softwareentwicklung Agentur in Tallinn',
    },
    heroSubtitle: {
      en: 'The world\'s most digital nation. Birthplace of Skype, Wise, and Bolt. EU member, e-Residency pioneer, and a dense concentration of top engineers.',
      fr: 'La nation la plus numérique au monde. Berceau de Skype, Wise et Bolt. Membre UE et pionnière de l\'e-Résidence.',
      es: 'La nación más digital del mundo. Cuna de Skype, Wise y Bolt. Miembro UE y pionera en e-Residencia.',
      de: 'Die digitalste Nation der Welt. Geburtsort von Skype, Wise und Bolt. EU-Mitglied und e-Residency-Pionier.',
    },
    whyTitle: {
      en: 'Why Choose Tallinn for Software Development',
      fr: 'Pourquoi Choisir Tallinn pour le Développement Logiciel',
      es: 'Por Qué Elegir Tallin para Desarrollo de Software',
      de: 'Warum Tallinn für Softwareentwicklung Wählen',
    },
    whyReasons: [
      { icon: '🏆', title: 'Digital Pioneer', description: 'Most startups per capita in Europe. Birthplace of Skype, TransferWise/Wise, Bolt, and Pipedrive.' },
      { icon: '🇪🇺', title: 'EU & NATO Member', description: 'Full EU data protection (GDPR), stable legal framework, and NATO cybersecurity standards.' },
      { icon: '💰', title: 'EU-Competitive Rates', description: 'Senior developers at $30-50/hr — significantly below Western EU rates with top-tier quality.' },
      { icon: '🎓', title: 'World-Class Education', description: 'TalTech, University of Tartu. Strong computer science and cybersecurity programs.' },
      { icon: '🌐', title: 'e-Residency Ecosystem', description: 'World\'s first e-Residency program. Easy to set up EU companies and digital business infrastructure.' },
      { icon: '🔒', title: 'Cybersecurity Excellence', description: 'NATO Cooperative Cyber Defence Centre of Excellence is in Tallinn. Top cybersecurity talent.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Tallinn?', answer: 'Tallinn is the digital capital of Europe — birthplace of Skype, Wise, Bolt. Dense engineering talent, EU member, and strong cybersecurity expertise.' },
      { question: 'How do rates compare to Western Europe?', answer: 'Senior developers at $30-50/hr vs $80-150/hr in London/Paris. Same EU standards, GDPR compliance, and often higher digital expertise.' },
      { question: 'What is e-Residency?', answer: 'Estonia\'s e-Residency lets you set up an EU company digitally. Many clients use this for seamless EU billing and contracting.' },
      { question: 'What technologies excel here?', answer: 'Fintech, blockchain, cybersecurity, govtech, and SaaS. Tallinn teams are especially strong in payment systems and digital identity.' },
      { question: 'Is Tallinn easy to reach?', answer: 'Direct flights from most European capitals. 2h from Helsinki, 1.5h from Stockholm. Excellent connectivity.' },
      { question: 'Data protection?', answer: 'Full EU GDPR compliance. NATO cybersecurity standards. Estonia has the strongest digital infrastructure in the EU.' },
    ],
    localRate: '€70-120',
    ourRate: '€30-50',
    savings: '50%+',
  },
};
